import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';
import { useNavigate } from 'react-router-dom';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { Button } from '../../components/widgets';
import { ADMIN_EDITABLE_STATUSES, ATTRIBUTION_TAB_IDS, STATUSES, TRANSLATE_PREFIX } from '../consts';
import { trackRequestSubmitted, trackRequestUpdated } from '../mixpanel';
import IntegrationRequestService from '../service';
import useConfirmDialog from '../useConfirmDialog';
import { useFieldValidityContext } from '../useFieldValidity';
import css from './FormActionBar.css';
import FormActionWarnings from './FormActionWarnings';

const SUBMIT_CONFIRM_PREFIX = `${TRANSLATE_PREFIX}.SUBMIT_CONFIRM_DIALOG`;

const SAVE_DRAFT_REQUIRES_PLATFORM_TEMPLATE_TOOLTIP = `${TRANSLATE_PREFIX}.TOOLTIPS.SAVE_DRAFT_REQUIRES_PLATFORM_TEMPLATE`;

const service = new IntegrationRequestService();
const BASE_PATH = '/react/integrations';

const FormActionBar = ({
    requestType,
    allowNextNavigation,
    titleKey,
    rules,
    onAddRequest,
    onUpdateRequest,
    readOnly,
    existingRequest,
    computedTabs,
    activeTabIndex,
    nextTab,
    onTabChange,
    onErrorClick,
}) => {
    const navigate = useNavigate();
    const { dirty, isValid, values, errors } = useFormikContext();
    const formData = values.formData || {};
    const fieldValidity = useFieldValidityContext();

    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const { confirm, dialog: confirmDialog } = useConfirmDialog();

    const isBusy = saving || submitting;
    const isAdminEditing = !!existingRequest && !readOnly && ADMIN_EDITABLE_STATUSES.includes(existingRequest.status);
    const isLastTab = !computedTabs || activeTabIndex === computedTabs.length - 1;
    const isDraftBlocked = rules.isDraftBlocked(formData, { fieldValidity, errors });
    const isSaveDraftDisabled = isBusy || !dirty || isDraftBlocked;
    // Submit must also respect async URL validity (and other draft-blocking conditions),
    // which formik's sync isValid doesn't cover.
    const isSubmitDisabled = isBusy || !isValid || isDraftBlocked;
    const isNextDisabled = isBusy || !nextTab || nextTab.disabled;
    const draftBlockedReasons =
        !isBusy && isDraftBlocked ? rules.getDraftBlockedReasons(formData, { fieldValidity }) : [];

    const platformTemplateMissing = !formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const showPlatformTemplateRequiredTooltip = isSaveDraftDisabled && platformTemplateMissing && !readOnly;

    const saveRequest = async () => {
        const data = values.formData;
        if (existingRequest) {
            const updated = await service.updateIntegrationRequest(existingRequest.id, { formData: data });
            onUpdateRequest(updated);
            return updated;
        }
        const created = await service.createIntegrationRequest({ requestType, formData: data });
        onAddRequest(created);
        return created;
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const saved = await saveRequest();
            trackRequestUpdated({
                requestType,
                formData: values.formData,
                status: saved.status,
                isAdmin: isAdminEditing,
            });
            allowNextNavigation();
            navigate(BASE_PATH, {
                state: { successMessage: { key: `${TRANSLATE_PREFIX}.MESSAGES.DRAFT_SAVED`, typeKey: titleKey } },
            });
        } catch {
            setError(`${TRANSLATE_PREFIX}.ERRORS.SAVE_DRAFT`);
        } finally {
            setSaving(false);
        }
    };

    const submitRequest = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const draft = await saveRequest();
            const submitted = await service.submitIntegrationRequest(draft.id);
            onUpdateRequest(submitted);
            trackRequestSubmitted({
                requestType,
                formData: values.formData,
            });
            allowNextNavigation();
            navigate(BASE_PATH, {
                state: { successMessage: { key: `${TRANSLATE_PREFIX}.MESSAGES.SUBMITTED`, typeKey: titleKey } },
            });
        } catch {
            setError(`${TRANSLATE_PREFIX}.ERRORS.SUBMIT_REQUEST`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = () => {
        if (!isValid) return;
        confirm({
            titleKey: `${SUBMIT_CONFIRM_PREFIX}.TITLE`,
            body: <Translate id={`${SUBMIT_CONFIRM_PREFIX}.BODY`} />,
            onConfirm: submitRequest,
        });
    };

    const handleNext = () => {
        if (nextTab && !nextTab.disabled) onTabChange(nextTab.id);
    };

    return (
        <div className={css.actionBar}>
            <FormActionWarnings
                error={error}
                isBusy={isBusy}
                isSaveDraftDisabled={isSaveDraftDisabled}
                readOnly={readOnly}
                existingRequest={existingRequest}
                isLastTab={isLastTab}
                computedTabs={computedTabs}
                draftBlockedReasons={draftBlockedReasons}
                suppressDraftBlockedPanel={showPlatformTemplateRequiredTooltip}
                onErrorClick={onErrorClick}
            />
            <div className={css.actionBarButtons}>
                <div className={css.actionBarPart}>
                    {readOnly ? (
                        isLastTab && (
                            <Button type="primary" onClick={() => navigate(BASE_PATH)}>
                                <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.DONE`} />
                            </Button>
                        )
                    ) : (
                        <>
                            {(!existingRequest || existingRequest.status === STATUSES.DRAFT) && (
                                <Button
                                    type="secondary"
                                    onClick={handleSave}
                                    disabled={isSaveDraftDisabled}
                                    tooltipKey={
                                        showPlatformTemplateRequiredTooltip
                                            ? SAVE_DRAFT_REQUIRES_PLATFORM_TEMPLATE_TOOLTIP
                                            : undefined
                                    }
                                >
                                    <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.${saving ? 'SAVING' : 'SAVE_DRAFT'}`} />
                                </Button>
                            )}
                            {isLastTab ? (
                                isAdminEditing ? (
                                    <Button
                                        type="primary"
                                        onClick={handleSave}
                                        disabled={isBusy || !dirty || !isValid || isDraftBlocked}
                                    >
                                        <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.${saving ? 'SAVING' : 'SAVE'}`} />
                                    </Button>
                                ) : (
                                    <Button type="primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
                                        <Translate
                                            id={`${TRANSLATE_PREFIX}.ACTIONS.${submitting ? 'SUBMITTING' : 'SUBMIT'}`}
                                        />
                                    </Button>
                                )
                            ) : null}
                        </>
                    )}
                    {!isLastTab && (
                        <Button
                            type="primary"
                            onClick={handleNext}
                            disabled={isNextDisabled}
                            tooltipKey={!isBusy && nextTab?.disabled ? nextTab.tooltipText : undefined}
                        >
                            <span className={css.buttonContent}>
                                <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.NEXT`} />
                                <Icon
                                    name="arrow-right"
                                    size={IconSizes.X_SMALL}
                                    interactive={false}
                                    variant={isNextDisabled ? IconVariants.LIGHT : IconVariants.WHITE}
                                />
                            </span>
                        </Button>
                    )}
                </div>
                <div className={css.actionBarPart}>
                    {computedTabs && activeTabIndex > 0 && (
                        <Button
                            type="secondary"
                            onClick={() => onTabChange(computedTabs[activeTabIndex - 1].id)}
                            disabled={isBusy}
                        >
                            <span className={css.buttonContent}>
                                <Icon name="arrow-left" size={IconSizes.X_SMALL} interactive={false} />
                                <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.BACK`} />
                            </span>
                        </Button>
                    )}
                </div>
            </div>
            {confirmDialog}
        </div>
    );
};

FormActionBar.propTypes = {
    requestType: PropTypes.string.isRequired,
    allowNextNavigation: PropTypes.func.isRequired,
    titleKey: PropTypes.string.isRequired,
    rules: PropTypes.shape({
        isDraftBlocked: PropTypes.func.isRequired,
        getDraftBlockedReasons: PropTypes.func.isRequired,
    }).isRequired,
    onAddRequest: PropTypes.func.isRequired,
    onUpdateRequest: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    existingRequest: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        status: PropTypes.string,
    }),
    computedTabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            tooltipText: PropTypes.string,
        })
    ),
    activeTabIndex: PropTypes.number.isRequired,
    nextTab: PropTypes.shape({
        id: PropTypes.string,
        disabled: PropTypes.bool,
        tooltipText: PropTypes.string,
    }),
    onTabChange: PropTypes.func.isRequired,
    onErrorClick: PropTypes.func,
};

export default FormActionBar;
